import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { actionContainerStyles, actionLinkStyles, actionHeaderStyles } from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import { API_ENDPOINT, DOCS_BASE_URL } from "../../../util/constant";

const LINK_HOVER_COLOR = "#F7941D";

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  background: #F8F8F8;
  border-radius: 5px;
  gap: 32px;
  box-shadow: 2px 5px 5px rgba(0,0,0,0.05);
  padding: 1.5em;
  min-height: 600px;
  margin-top: 30px;
  overflow-x: hidden;
  align-items: flex-start;

  @media (min-width: ${breakpoints.tablet}px) {
    flex-wrap: nowrap;
  }

  @media (max-width: ${breakpoints.tablet}px) {
    gap: 24px;
    flex-direction: column;
    padding: 1.25em;
    width: 400px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    flex-direction: column;
    gap: 16px;
    padding: 1em;
    min-height: auto;
  }
`;

const Actions = styled.div`
  ${actionContainerStyles};
  // border: 1px solid ${({ colors }) => colors.border};

  @media (min-width: ${breakpoints.tablet}px) {
    flex: 0 0 260px;
  }

  h3 {
    ${actionHeaderStyles};
    border-bottom: 1px solid ${({ colors }) => colors.border};
  }

  ul {
    margin: 0;
    list-style: none;
    padding: 12px 16px;
  }

  li + li {
    margin-top: 4px;
  }

  a {
    ${actionLinkStyles};
    padding-left: 16px;
    text-decoration: none !important;
    color: #2C4960 !important;

    &:hover {
      color: ${LINK_HOVER_COLOR};
    }
  }
`;

const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
  background: #fff;
  overflow-x: hidden;

  @media (max-width: ${breakpoints.tablet}px) {
    padding: 1em;
    border-radius: 5px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    width: 100%;
    overflow-y: visible;
  }

  @media (max-width: ${breakpoints.phone}px) {
    width: 400px;
    height:auto;
    padding: 0.75em;
    overflow: auto;
    overflow-y: visible;
  }
`;

const NavHeader = styled.div`
  margin-bottom: 1em;

  h2 {
    margin: 0;
    font-size: 1.2em;
    display: inline-block;
    color: ${({ colors }) => colors.labelText};
  }

  span {
    display: block;

    @media (max-width: ${breakpoints.phone}px) {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      white-space: nowrap;
    }
  }
`;

const NavButtons = styled.div`
  display: inline-flex;
  gap: 0.5em;

  @media (max-width: ${breakpoints.phone}px) {
    display: inline-flex;
    overflow-x: auto;
    white-space: nowrap;
    gap: 0.5em;
    padding-bottom: 0.5em;
  }
`;

const NavButton = styled.button`
  padding: 0.4em 0.8em;
  font-size: 0.9em;
  font-weight: bolder;
  background: ${({ active, colors }) =>
    active ? colors.buttonBackground : colors.buttonBackground};
  color: ${({ active, colors }) =>
    active ? "white" : colors.titleText};
  border: 1px solid ${({ colors }) => colors.border};
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
    color: white;
  }
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 500px;
  margin-top: 20px;
  border: 1px solid #ccc;

  @media (max-width: ${breakpoints.phone}px) {
    height: 300px;
    width: 300px;
  }
`;

const FaqContainer = styled.div`
  width: 100%;
  margin-top: 20px;
  border: 1px solid #ccc;
  padding: 10px;
  color: #050505;
  font-family: Verdana, Geneva, sans-serif;
  font-size: 13px;
  overflow-y: auto;
  max-height: 520px;

  @media (max-width: ${breakpoints.tablet}px) {
    max-height: none;
    overflow-y: visible;
  }

  @media (max-width: ${breakpoints.phone}px) {
    height: auto;
    max-height: none;
    width: 370px;
    overflow-y: visible;
  }
`;

const sections = [
  { key: "onboarding",          label: "Onboarding" },
  { key: "onlineregistration",  label: "Online Reg" },
  { key: "teachersverify",      label: "Teachers Verify" },
  { key: "reports",             label: "Reports" },
  { key: "addadmins",           label: "Add Admins" },
];

const FAQ_DOCS_BASE_URL = `${DOCS_BASE_URL}/faq`;

const printableLinks = [
  {
    label: "Create a STAT",
    href: `${FAQ_DOCS_BASE_URL}/isafe-create-stat.pdf`,
  },
  {
    label: "Online Registration",
    href: `${FAQ_DOCS_BASE_URL}/isafe-online-registration.pdf`,
  },
  {
    label: "Verify Implementation",
    href: `${FAQ_DOCS_BASE_URL}/isafe-verify-implementation.pdf`,
  },
  {
    label: "Generate Reports",
    href: `${FAQ_DOCS_BASE_URL}/isafe-generate-reports.pdf`,
  },
  {
    label: "Add Administrators",
    href: `${FAQ_DOCS_BASE_URL}/isafe-add-administrators.pdf`,
  },
];

export default function UserGuideContent({ colors }) {
  const [section, setSection] = useState("");
  const [html, setHtml]       = useState("");

  useEffect(() => {
    fetch("/faq.html")
      .then(res => res.text())
      .then((text) => {
        const resolvedHtml = API_ENDPOINT
          ? text.replace(/__API_ENDPOINT__/g, API_ENDPOINT)
          : text;
        setHtml(resolvedHtml);
      })
      .catch(console.error);
  }, []);

  return (
    <Container>
      <Actions colors={colors}>
        <h3>Printable Version</h3>
        <ul>
          <li><a href={`${FAQ_DOCS_BASE_URL}/isafe-create-stat.pdf`} target="_blank" rel="noreferrer">Create a STAT</a></li>
          <li><a href={`${FAQ_DOCS_BASE_URL}/isafe-online-registration.pdf`} target="_blank" rel="noreferrer">Online Registration</a></li>
          <li><a href={`${FAQ_DOCS_BASE_URL}/isafe-verify-implementation.pdf`} target="_blank" rel="noreferrer">Verify Implementation</a></li>
          <li><a href={`${FAQ_DOCS_BASE_URL}/isafe-generate-reports.pdf`} target="_blank" rel="noreferrer">Generate Reports</a></li>
          <li><a href={`${FAQ_DOCS_BASE_URL}/isafe-add-administrators.pdf`} target="_blank" rel="noreferrer">Add Administrators</a></li>
        </ul>
      </Actions>

      <View>
        <NavHeader colors={colors}>
          <h2>Help for:</h2>
          <span>
            <NavButtons>
              {sections?.map((s) => (
                <NavButton
                  key={s.key}
                  colors={colors}
                  active={section === s.key}
                  onClick={() => setSection(s.key)}
                >
                  {s.label}
                </NavButton>
              ))}
            </NavButtons>
          </span>
        </NavHeader>

        <FaqContainer
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </View>
    </Container>
  );
}
